import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  FileText, 
  Monitor, 
  Printer, 
  Maximize2,
  RotateCw,
  Check
} from 'lucide-react';
import {
  PageTemplate,
  PAGE_TEMPLATES,
  getTemplatesByCategory,
  flipOrientation,
  createCustomTemplate,
  validateDimensions,
} from '../../types/pageTemplates';

interface PageTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: PageTemplate) => void;
  className?: string;
}

export const PageTemplateModal: React.FC<PageTemplateModalProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
  className = '',
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<PageTemplate | null>(null);
  const [customWidth, setCustomWidth] = useState<string>('800');
  const [customHeight, setCustomHeight] = useState<string>('600');
  const [validationError, setValidationError] = useState<string>('');

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'document': return <FileText className="w-4 h-4" />;
      case 'presentation': return <Monitor className="w-4 h-4" />;
      case 'print': return <Printer className="w-4 h-4" />;
      case 'custom': return <Maximize2 className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const handleTemplateSelect = (template: PageTemplate) => {
    setSelectedTemplate(template);
    setValidationError('');
    
    if (template.id === 'custom') {
      // Don't auto-select custom template, wait for user to set dimensions
      return;
    }
    
    // Auto-select non-custom templates
    onSelectTemplate(template);
    onClose();
  };

  const handleCustomDimensionsApply = () => {
    const width = parseInt(customWidth);
    const height = parseInt(customHeight);
    
    const validation = validateDimensions(width, height);
    if (!validation.valid) {
      setValidationError(validation.error || 'Invalid dimensions');
      return;
    }

    setValidationError('');
    
    const customTemplate = createCustomTemplate(width, height);
    onSelectTemplate(customTemplate);
    onClose();
  };

  const handleOrientationFlip = (template: PageTemplate) => {
    const flippedTemplate = flipOrientation(template);
    onSelectTemplate(flippedTemplate);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className={`bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden ${className}`}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Choose Page Template</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="space-y-8">
              {/* Template Categories */}
              {['document', 'presentation', 'print', 'custom'].map((category) => {
                const templates = getTemplatesByCategory(category as PageTemplate['category']);
                if (templates.length === 0) return null;

                return (
                  <div key={category} className="space-y-4">
                    <div className="flex items-center gap-3">
                      {getCategoryIcon(category)}
                      <h3 className="text-lg font-medium text-gray-900 capitalize">
                        {category} Templates
                      </h3>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {templates.map((template) => (
                        <motion.div
                          key={template.id}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                            selectedTemplate?.id === template.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                          onClick={() => handleTemplateSelect(template)}
                        >
                          {/* Template Preview */}
                          <div className="mb-3 flex items-center justify-center">
                            <div
                              className="border border-gray-300 bg-white shadow-sm"
                              style={{
                                width: Math.min(template.dimensions.width / 8, 120),
                                height: Math.min(template.dimensions.height / 8, 80),
                                minWidth: 60,
                                minHeight: 40,
                              }}
                            />
                          </div>
                          
                          {/* Template Info */}
                          <div className="text-center">
                            <h4 className="font-medium text-gray-900 mb-1">{template.name}</h4>
                            <p className="text-sm text-gray-500 mb-2">{template.description}</p>
                            <div className="text-xs text-gray-400">
                              {template.dimensions.width} × {template.dimensions.height} px
                            </div>
                            <div className="text-xs text-gray-400 capitalize">
                              {template.orientation}
                            </div>
                          </div>

                          {/* Orientation Flip Button */}
                          {template.id !== 'custom' && (
                            <div className="mt-3 flex justify-center">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOrientationFlip(template);
                                }}
                                className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                              >
                                <RotateCw className="w-3 h-3" />
                                Flip
                              </button>
                            </div>
                          )}

                          {/* Selected Indicator */}
                          {selectedTemplate?.id === template.id && (
                            <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full p-1">
                              <Check className="w-3 h-3" />
                            </div>
                          )}
                        </motion.div>
                      ))}
                    </div>

                    {/* Custom Dimensions Input */}
                    {category === 'custom' && selectedTemplate?.id === 'custom' && (
                      <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                        <h4 className="font-medium text-gray-900">Custom Dimensions</h4>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm text-gray-600 mb-1">Width (px)</label>
                            <input
                              type="number"
                              value={customWidth}
                              onChange={(e) => setCustomWidth(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              min="100"
                              max="5000"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-600 mb-1">Height (px)</label>
                            <input
                              type="number"
                              value={customHeight}
                              onChange={(e) => setCustomHeight(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              min="100"
                              max="5000"
                            />
                          </div>
                        </div>
                        
                        {validationError && (
                          <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                            {validationError}
                          </div>
                        )}
                        
                        <button
                          onClick={handleCustomDimensionsApply}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Apply Custom Size
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PageTemplateModal;
